/* Build credit — "Built by / Greenlit by" (Viper handoff). Resolves a build's
   builder/approver to a real team member via MAV.memberById, falling back to the
   legacy `owner` display string so it renders even before a row carries
   builderId/approvedById. Shared with jobdetail.jsx via the window export below. */
function MiniAvatar({ m, size = 18 }) {
  if (!m) return null;
  const ink = m.color === 'var(--grad-warm)' ? 'var(--ink-on-brand)' : '#fff';
  return <span className="mini-av" style={{ width: size, height: size, fontSize: size * 0.42, background: m.color, color: ink }}>{m.initials}</span>;
}

// Compact display name: "Jordan Diaz" → "J. Diaz"; leave already-short forms ("A. Rao").
function shortName(name) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0].length > 2 && !parts[0].endsWith('.')) {
    return parts[0][0] + '. ' + parts.slice(1).join(' ');
  }
  return name;
}

// Resolve a builder/approver id (or legacy owner name) to a team member.
// Resolve against the LIVE MAV.team by id FIRST: ids are ext_id slugs ('as','jd').
// MAV.memberById is a stale closure over the *mock* roster (hydrate never re-points
// it), so after a live hydrate it can't see real members — search MAV.team directly,
// keep memberById as the offline/mock fallback, then name-match, then synthesize.
function creditMember(idOrName) {
  const MAV = window.MAV;
  if (!idOrName) return null;
  const team = MAV.team || [];
  // "You" is the legacy self-credit string (hydrate.js sets owner:'You' for any
  // build with no resolvable builder_name). It means "the signed-in user" — so
  // resolve it to MAV.currentUser, NOT a hardcoded 'jd'/'JD'. The old dev account
  // was renamed Jordan Diaz → Aum Shukla (2026-06-01), so the hardwired 'JD' put a
  // stale placeholder on every un-attributed live build (and a second name for the
  // same person). currentUser is the live /me object ({ext_id,name,initials}) or,
  // offline, the mock 'jd' string — both resolve back to the real member here.
  if (idOrName === 'You') {
    const cu = MAV.currentUser;
    const ext = (cu && cu.ext_id) || (typeof cu === 'string' ? cu : null);
    if (ext) {
      const m = team.find(t => t.id === ext) || (MAV.memberById && MAV.memberById(ext));
      if (m) return m;
    }
    // The live /me object has no ext_id, so also resolve by display name → the SAME
    // member a builderId row resolves to. Keeps a self-built row's avatar (color +
    // initials) identical whether it carried a real builder_name or the 'You' fallback.
    const byName = (cu && cu.name && team.find(t => t.name === cu.name)) || (typeof cu === 'string' && team.find(t => t.name === cu)) || null;
    if (byName) return byName;
    if (cu && cu.name) return { name: cu.name, initials: cu.initials || (cu.name.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || '?'), color: 'var(--grad-warm)' };
    return null;   // pre-hydrate / no session — render '—', never a stale identity
  }
  let m = team.find(t => t.id === idOrName)
       || (MAV.memberById && MAV.memberById(idOrName))
       || team.find(t => t.name === idOrName);
  if (m) return m;
  const initials = idOrName.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || '?';
  return { name: idOrName, initials, color: 'var(--queued)' };
}

// True display status for a build row. A build whose QA has finished — it carries a real
// verdict, OR its QA stage is `done`, OR every stage is done/review — cannot still be
// "running"/"queued"; that's just the projector not having folded the terminal status yet
// (a stale/lagging backend leaves a done build stuck showing "Running"). Derive the real
// terminal state (greenlit → complete, else needs_review) so the pill + tab filter stop
// lying. Mirrors the status/verdict semantics + jobdetail's effectiveStatus, list-scoped
// (verdict + stage snapshot only). A genuinely in-flight build, or a never-started `queued`
// zombie (no verdict, QA not done), is returned unchanged.
function effStatus(j) {
  if (!j) return j;
  if (j.status !== 'running' && j.status !== 'queued') return j.status;
  const stages = Array.isArray(j.stages) ? j.stages : [];
  const finishedQA = /^(PASS|FAIL|CONDITIONAL)$/i.test(j.verdict || '')
    || (stages.find(s => s.id === 'qa') || {}).status === 'done'
    || (stages.length > 0 && stages.every(s => s.status === 'done' || s.status === 'review'));
  if (!finishedQA) return j.status;
  const greenlit = !!j.approvedById || (stages.find(s => s.id === 'approve') || {}).status === 'done';
  return greenlit ? 'complete' : 'needs_review';
}

function BuildCredit({ job }) {
  const builder = creditMember(job.builderId || job.owner);
  const approver = job.approvedById ? creditMember(job.approvedById) : null;
  const st = effStatus(job);
  let pending = 'Awaiting review';
  if (st === 'running' || st === 'queued') pending = 'In build';
  else if (st === 'failed') pending = 'Not greenlit';
  return (
    <div className="credit">
      <div className="credit-row" title={'Built by ' + (builder ? builder.name : 'unknown')}>
        <I.builds size={12} className="credit-ico" />
        <MiniAvatar m={builder} />
        <span className="credit-nm">{builder ? shortName(builder.name) : '—'}</span>
      </div>
      <div className="credit-row" title={approver ? 'Greenlit by ' + approver.name : pending}>
        <I.check size={12} className={'credit-ico' + (approver ? ' ok' : '')} />
        {approver
          ? <React.Fragment><MiniAvatar m={approver} /><span className="credit-nm">{shortName(approver.name)}</span></React.Fragment>
          : <span className="credit-pending">{pending}</span>}
      </div>
    </div>
  );
}

function BuildsList({ openJob }) {
  const { jobs } = window.MAV;
  // RBAC-07 GAP-5: in an aggregate context (org / "all my teams") the rows carry a
  // workspace, so we add an attribution column + a context-aware scope line. Single
  // tenant → no column (attribution is implicit). useViewer re-renders on a switch.
  const v = (window.RBAC && window.RBAC.useViewer) ? window.RBAC.useViewer() : (window.RBAC && window.RBAC.viewer && window.RBAC.viewer());
  const aggregate = !!(window.RBAC && window.RBAC.isAggregateView && window.RBAC.isAggregateView());
  const [filter, setFilter] = React.useState('all');
  const filters = [['all', 'All'], ['running', 'Running'], ['needs_review', 'Needs review'], ['complete', 'Passed'], ['failed', 'Failed']];
  const shown = filter === 'all' ? jobs : jobs.filter(j => effStatus(j) === filter);

  let scopeDesc = 'All jobs across the workspace.';
  if (v) {
    if (v.ctx === 'org') scopeDesc = 'All builds across every workspace · organization view.';
    else if (v.ctx === 'all') scopeDesc = 'Builds across the ' + (v.led ? v.led.length : 0) + ' teams you lead · aggregated.';
  }

  return (
    <div className="page fade-in">
      <div className="page-head">
        <div><h1 className="page-title">Builds</h1><p className="page-desc">{scopeDesc}</p></div>
      </div>
      <div className="row gap-8 wrap" style={{ marginBottom: 18 }}>
        {filters.map(([id, l]) => (
          <button key={id} className={'chip' + (filter === id ? '' : '')} onClick={() => setFilter(id)}
            style={filter === id ? { background: 'var(--ink)', color: 'var(--surface)' } : { cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      <div className="card card-pad tbl-scroll">
        <table className="tbl">
          <thead><tr><th>Build</th>{aggregate && <th>Workspace</th>}<th>Client</th><th>Source</th><th>Status</th><th>Verdict</th><th>Tokens / $</th><th>Built / Greenlit</th><th>Started</th><th></th></tr></thead>
          <tbody>
            {shown.map(j => {
              const c = window.MAV.clientBySlug(j.client) || { name: j._org ? '—' : 'Unassigned' };
              return (
                <tr key={j.id} className="clickable" onClick={() => openJob(j.id)}>
                  <td><div className="nm">{(j.name || '').length > 28 ? j.name.slice(0, 28) + '…' : (j.name || j.id)}</div><div className="mono" style={{ fontSize: 11 }}>{j.id}</div></td>
                  {aggregate && <td>{window.WorkspaceTag ? <WorkspaceTag job={j} /> : null}</td>}
                  <td><div className="row gap-8"><ClientBadge slug={j.client} size={24} /><span>{c.name}</span></div></td>
                  <td><div className="row gap-6" style={{ flexWrap: 'wrap' }}><span className="chip">{j.source ? j.source.toUpperCase() : '—'}</span>{(() => {
                    // Build-engine tag (feature 4). Shown ONLY when job.engine is
                    // known — an unset engine renders no tag (never mislabels a
                    // legacy build as Codex). router→Bullseye, legacy→Swarm.
                    const e = j.engine; if (!e) return null;
                    const L = { codex: 'Codex', bullseye: 'Bullseye', router: 'Bullseye', legacy: 'Swarm', claude: 'Claude' }[e] || e;
                    return <span className={'eng-tag eng-' + e} title={'Build engine: ' + L}>{L}</span>;
                  })()}</div></td>
                  <td><StatusPill status={effStatus(j)} verdict={j.verdict} /></td>
                  <td>{j.verdict ? <span className={'mono ' + (j.verdict === 'PASS' ? '' : '')} style={{ fontSize: 12, fontWeight: 700, color: j.verdict === 'PASS' ? 'var(--pass)' : j.verdict === 'FAIL' ? 'var(--fail)' : 'var(--cond)' }}>{j.verdict}</span> : <span className="muted mono" style={{ fontSize: 12 }}>—</span>}</td>
                  <td className="mono">{(j.tokensK ?? 0)}K · ${(j.cost ?? 0).toFixed(2)}</td>
                  <td><BuildCredit job={j} /></td>
                  <td className="muted" style={{ fontSize: 12.5 }}>{j.started}</td>
                  <td><I.chevron size={15} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewQueue({ openJob }) {
  const reviewJobs = window.MAV.jobs.filter(j => effStatus(j) === 'needs_review');
  return (
    <div className="page fade-in">
      <div className="page-head"><div><h1 className="page-title">Review queue</h1><p className="page-desc">Everything blocked on a human, oldest first.</p></div></div>
      <div className="card card-pad" style={{ maxWidth: 720 }}>
        {reviewJobs.map(j => {
          const c = window.MAV.clientBySlug(j.client);
          return (
            <div key={j.id} className="nr-item" onClick={() => openJob(j.id, 'review')}>
              <div className="nr-stripe" style={{ background: 'var(--cond)' }}></div>
              <ClientBadge slug={j.client} size={36} />
              <div className="grow" style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{j.name}</div>
                <div className="muted" style={{ fontSize: 12 }}>{c.name} · CONDITIONAL · confidence {Math.round(j.confidence * 100)}% · {j.started}</div>
                {(() => { const b = creditMember(j.builderId || j.owner); return b
                  ? <div className="row gap-6" style={{ marginTop: 5, alignItems: 'center' }}>
                      <I.builds size={12} className="credit-ico" /><MiniAvatar m={b} size={16} />
                      <span className="credit-nm" style={{ fontSize: 11.5 }}>Built by {b.name}</span>
                    </div>
                  : null; })()}
              </div>
              <button className="btn btn-primary">Review <I.arrow size={15} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Placeholder({ title, desc }) {
  return (
    <div className="page fade-in">
      <div className="page-head"><div><h1 className="page-title">{title}</h1><p className="page-desc">{desc}</p></div></div>
      <div className="card card-pad" style={{ display: 'grid', placeItems: 'center', minHeight: 340, textAlign: 'center' }}>
        <div>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--surface)', boxShadow: 'var(--nm-out)', display: 'grid', placeItems: 'center', margin: '0 auto 16px', color: 'var(--brand-orange)' }}><I.layers size={26} /></div>
          <p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{title} — coming in this pass next</p>
          <p className="muted" style={{ fontSize: 13, maxWidth: 380, margin: '8px auto 0' }}>{desc} The design language, components and states are all defined; this surface just needs wiring.</p>
        </div>
      </div>
    </div>
  );
}
Object.assign(window, { BuildsList, ReviewQueue, Placeholder, BuildCredit, MiniAvatar, shortName, creditMember, effStatus });
